import React, { useState } from 'react';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * NfcOfflineActions — Write profile URL to NFC tag + download offline copy
 *
 * Props:
 *   profileUrl  — full public URL (e.g. https://phrid.com/pet/pet-abc123)
 *   offlineUrl  — API URL for offline HTML (e.g. /api/offline/pet/pet-abc123)
 *   profileName — display name (e.g. "Buddy")
 *   type        — 'pet' or 'user'
 */
export default function NfcOfflineActions({ profileUrl, offlineUrl, profileName, type = 'pet' }) {
  const [nfcStatus, setNfcStatus] = useState('idle'); // idle | waiting | success | error | unsupported
  const [downloading, setDownloading] = useState(false);

  const supportsNfc = 'NDEFReader' in window;

  // ── Write URL to NFC tag ────────────────────────────────────
  const writeNfc = async () => {
    if (!supportsNfc) {
      setNfcStatus('unsupported');
      return;
    }

    setNfcStatus('waiting');
    try {
      const ndef = new window.NDEFReader();
      await ndef.write({
        records: [
          { recordType: 'url', data: profileUrl }
        ]
      });
      setNfcStatus('success');
      toast.success('Profile written to NFC tag!');
      setTimeout(() => setNfcStatus('idle'), 4000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('NFC permission denied. Please allow NFC access in your browser settings.');
      } else if (err.name === 'AbortError') {
        // User cancelled
        setNfcStatus('idle');
        return;
      } else {
        toast.error('Failed to write NFC tag. Make sure a tag is nearby.');
      }
      setNfcStatus('error');
      setTimeout(() => setNfcStatus('idle'), 3000);
    }
  };

  // ── Download offline HTML ───────────────────────────────────
  const downloadOffline = async () => {
    setDownloading(true);
    try {
      const fullUrl = `${API_BASE}${offlineUrl}`;
      const res = await fetch(fullUrl);
      if (!res.ok) throw new Error('Failed to generate offline profile');

      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(profileName || 'profile').replace(/[^a-zA-Z0-9]/g, '-')}-offline.html`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Offline profile downloaded');
    } catch {
      toast.error('Failed to download offline profile');
    } finally {
      setDownloading(false);
    }
  };

  // ── Status messages ─────────────────────────────────────────
  const statusMsg = {
    idle: null,
    waiting: '📡 Hold your phone against the NFC tag…',
    success: '✅ Profile URL written to NFC tag!',
    error: '❌ Write failed. Try holding the tag closer.',
    unsupported: null
  };

  return (
    <div className="nfc-offline-section">
      <div className="nfc-section-header">
        <h3>📱 NFC & Offline Access</h3>
        <p>
          Write this profile to an NFC tag so anyone can tap their phone to view it,
          or download an offline copy that works without internet.
        </p>
      </div>

      <div className="nfc-actions">
        {/* NFC Write */}
        <div className="nfc-action-card">
          <div className="nfc-action-icon">📡</div>
          <div className="nfc-action-body">
            <h4>Write to NFC Tag</h4>
            <p>
              {supportsNfc
                ? 'Tap an NFC tag against your phone to write the profile URL.'
                : 'NFC writing requires Chrome on Android. On iOS, use an NFC writer app with this URL:'}
            </p>
            {!supportsNfc && (
              <div className="nfc-url-copy">
                <code>{profileUrl}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(profileUrl); toast.success('URL copied!'); }}
                  className="nfc-copy-btn"
                >
                  Copy
                </button>
              </div>
            )}
            {supportsNfc && (
              <button
                className={`nfc-btn nfc-btn-primary ${nfcStatus === 'waiting' ? 'pulse' : ''}`}
                onClick={writeNfc}
                disabled={nfcStatus === 'waiting'}
              >
                {nfcStatus === 'waiting' ? '📡 Waiting for tag…' :
                 nfcStatus === 'success' ? '✅ Written!' :
                 '📲 Write to NFC'}
              </button>
            )}
          </div>
        </div>

        {/* Offline Download */}
        <div className="nfc-action-card">
          <div className="nfc-action-icon">💾</div>
          <div className="nfc-action-body">
            <h4>Save Offline Copy</h4>
            <p>
              Download a self-contained HTML file with all {type === 'pet' ? `${profileName}'s` : 'your'} public profile data.
              Works without internet — save it to your phone or share it.
            </p>
            <button
              className="nfc-btn nfc-btn-secondary"
              onClick={downloadOffline}
              disabled={downloading}
            >
              {downloading ? 'Generating…' : '💾 Download Offline Profile'}
            </button>
          </div>
        </div>

        {/* Share */}
        {'share' in navigator && (
          <div className="nfc-action-card">
            <div className="nfc-action-icon">🔗</div>
            <div className="nfc-action-body">
              <h4>Share Profile Link</h4>
              <p>Send the live profile link via any app on your phone.</p>
              <button
                className="nfc-btn nfc-btn-secondary"
                onClick={() => {
                  navigator.share({ title: `${profileName} — Health Profile`, url: profileUrl })
                    .catch(() => {});
                }}
              >
                🔗 Share Link
              </button>
            </div>
          </div>
        )}
      </div>

      {statusMsg[nfcStatus] && (
        <div className={`nfc-status ${nfcStatus}`}>
          {statusMsg[nfcStatus]}
        </div>
      )}

      <style>{`
        .nfc-offline-section { margin-top: 1.5rem; }
        .nfc-section-header { margin-bottom: 1rem; }
        .nfc-section-header h3 { font-size: 1.05rem; color: var(--db-text); margin-bottom: .25rem; }
        .nfc-section-header p { font-size: .85rem; color: var(--db-text3); margin: 0; }
        .nfc-actions { display: flex; flex-direction: column; gap: .75rem; }
        .nfc-action-card {
          display: flex; gap: .85rem; align-items: flex-start;
          padding: 1rem 1.1rem;
          background: var(--db-surface2); border: 1.5px solid var(--db-border); border-radius: 10px;
          transition: border-color .15s;
        }
        .nfc-action-card:hover { border-color: var(--db-accent); }
        .nfc-action-icon { font-size: 1.5rem; flex-shrink: 0; margin-top: 2px; }
        .nfc-action-body { flex: 1; min-width: 0; }
        .nfc-action-body h4 { font-size: .9rem; font-weight: 700; color: var(--db-text); margin-bottom: .25rem; }
        .nfc-action-body p { font-size: .8rem; color: var(--db-text3); margin: 0 0 .6rem; line-height: 1.45; }

        .nfc-btn {
          display: inline-flex; align-items: center; gap: .4rem;
          padding: 8px 16px; border-radius: 8px; font-size: .85rem; font-weight: 600;
          cursor: pointer; border: none; transition: all .15s; font-family: inherit;
          white-space: nowrap;
        }
        .nfc-btn-primary { background: var(--db-accent); color: var(--db-contrast); }
        .nfc-btn-primary:hover:not(:disabled) { opacity: .88; }
        .nfc-btn-primary.pulse { animation: nfcPulse 1.5s ease-in-out infinite; }
        .nfc-btn-secondary { background: var(--db-border2); color: var(--db-text2); border: 1px solid var(--db-border); }
        .nfc-btn-secondary:hover:not(:disabled) { background: var(--db-border); }
        .nfc-btn:disabled { opacity: .55; cursor: not-allowed; }

        .nfc-url-copy {
          display: flex; gap: .5rem; align-items: center; margin-bottom: .5rem;
          background: var(--db-surface); border: 1px solid var(--db-border); border-radius: 6px; padding: 6px 10px;
        }
        .nfc-url-copy code {
          flex: 1; font-size: .72rem; color: var(--db-accent); word-break: break-all;
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
        }
        .nfc-copy-btn {
          background: var(--db-accent); color: var(--db-contrast); border: none; border-radius: 4px;
          padding: 3px 10px; font-size: .75rem; font-weight: 600; cursor: pointer;
          font-family: inherit; flex-shrink: 0;
        }

        .nfc-status {
          margin-top: .75rem; padding: 10px 14px; border-radius: 8px;
          font-size: .85rem; font-weight: 600; text-align: center;
        }
        .nfc-status.waiting { background: var(--db-tint-bg); color: var(--db-blue-text); border: 1px solid var(--db-blue-border); }
        .nfc-status.success { background: var(--db-success-bg); color: var(--db-success-text); border: 1px solid var(--db-success-text); }
        .nfc-status.error { background: var(--db-danger-soft-bg); color: var(--db-danger-text); border: 1px solid var(--db-danger-solid); }

        @keyframes nfcPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(102,126,234,.5); }
          50% { box-shadow: 0 0 0 10px rgba(102,126,234,0); }
        }

        @media (max-width: 520px) {
          .nfc-action-card { flex-direction: column; }
          .nfc-action-icon { font-size: 1.3rem; }
        }
      `}</style>
    </div>
  );
}
